// The practice **Run** sequence: one request, a local season, one summary.
//
// It exists as a module rather than inside the composition root for the reason
// `ui/rounds/selection-request.ts` already establishes: the sequence has promises in it, and a
// promise sequence that lives in a component is a sequence only a rendered DOM can reach. Everything
// here is DOM-free and React-free.
//
// ## The accounting is the contract
//
// `client-app` requires that a practice run make **exactly two** server calls and issue **no**
// batch-run request and **no** trace-read request. That is a property of this file plus the port the
// root builds: the two calls are `startLocalRun` and `submitRunSummary`, the stepping is the
// injected worker's, and the trace comes back out of the device's own store.
//
// ## Why it satisfies the shipped `ColonyRunController`
//
// The sandbox coordinator owns the *ordering* of a run's phases — submit, admitted, writing,
// terminal, load — and those are identical whether the season was stepped in a datacentre or on the
// player's phone. Handing it the shipped seam therefore keeps the whole reducer, the transport
// control's six states and the written-extent model unchanged, which is what makes practice reuse the
// Sandbox screen rather than needing one of its own.
//
// Two additions the seam does not carry, exposed beside it rather than smuggled into it:
//
//   * `progressFor(runId)` — the port's progress read, answered from the **worker's** reports rather
//     than a server poll. Determinate against the horizon the run-start response named.
//   * `cancel()` — cooperative, and the device's own; there is no server-side run to cancel.
//
// ## The two rules `submit()` holds in tension
//
// `rounds-local-run` "The document is compiled locally, and a run still starts under server
// authority" pulls in two directions at once, and `submit()` is where both land:
//
//   * The document is compiled **on this device, before any request is made**, so a non-compiling
//     draft is refused instantly with the compiler's own words and the server never sees a request it
//     would only reject. That works with no network at all.
//   * A run nonetheless begins stepping **only** after the single run-start request is accepted,
//     because that response carries the **run token**. Local compilation buys latency and honest
//     diagnostics, not offline play: a tokenless completed run would either be discarded silently or
//     admitted unbound, and neither is acceptable.
//
// The second is enforced structurally rather than by a check: `submit()` records the admitted run and
// returns, and `awaitCompletion()` is what constructs the worker and steps. The driver's `run()`
// steps on entry, so "no tick before the token is held" is satisfied by *not calling it*.
//
// The token is held here, per run, because the summary submission needs it and nothing else does.

import type {
  ColonyRunController,
  RunCandidate,
  RunOutcome,
  RunSubmission,
} from '../../ui/colony/run-controller';
import type { RunStatus } from '../../net/rounds-http';
import type { RunProgressDto } from '../../ui/rounds/sandbox-coordinator';
import type {
  PracticeDocumentCompiler,
  PracticeInFlightView,
  PracticeRunFailure,
  PracticeRunResult,
  PracticeWorkerFactory,
} from './practice-runner';
import type { PracticeRunTransport } from '../../net/rounds-practice-http';

export interface PracticeRunControllerDeps {
  transport: PracticeRunTransport;
  /**
   * **The on-device compile gate, and it runs before `transport.startLocalRun`.**
   *
   * That ordering is the requirement, not an optimisation: `rounds-local-run` says a non-compiling
   * document is refused "no request sent, no trace written, no run recorded", and
   * `client-sandbox-screen` adds that the diagnostics reach the player "without a round trip". Both
   * are false if the compile happens after the request, and both would still *appear* satisfied —
   * the diagnostics would arrive either way — which is why the test that pins it asserts the
   * transport's call log is **empty**, not merely that a refusal was returned.
   *
   * Required, with no default: see {@link PracticeDocumentCompiler}.
   */
  compile: PracticeDocumentCompiler;
  /** Built per run and terminated when it settles, so a crashed worker cannot poison the next run
   * (`rounds-local-run` "A worker crash is contained"). */
  createWorker: PracticeWorkerFactory;
  /** Called whenever the device reports progress, so the root can nudge the reducer without polling
   * anything. Optional: the progress is readable through `progressFor` regardless. */
  onProgress?: (runId: number) => void;
  /**
   * Called whenever the device reports the extent it has **already written**, so the run can be
   * watched while it steps (`rounds-trace`'s in-process carve-out; {@link PracticeInFlightView}
   * carries the reading it rests on).
   *
   * Deliberately **not** folded into {@link onProgress}. Progress is a count the reducer re-reads
   * through `progressFor`, and is what the scrub track's written extent is built from; a watched view
   * is world state and is pushed. Optional in the same sense as `onProgress`: a root that supplies
   * none gets a run that writes, publishes and reports and is simply not watched — which is the state
   * every server-executed run is permanently in.
   */
  onWatch?: (runId: number, view: PracticeInFlightView) => void;
}

export interface PracticeRunController extends ColonyRunController {
  /** The port's progress read — the device's own count, never a server poll. `null` for a run this
   * controller does not know, which is what lets the reducer decline to move the track. */
  progressFor(runId: number): RunProgressDto | null;
  /** Cancels the run in flight, if any. Cooperative: it takes effect within a tick. */
  cancel(): void;
}

/** One admitted run's whole local state. */
interface AdmittedRun {
  runId: number;
  documentVersion: number;
  token: string;
  terminalTick: number;
  document: string;
  /** The world the start response named, held here rather than re-read at execution — the balance is
   * part of the configuration frozen at launch, and the response that named it has already been
   * consumed by then. Both `undefined` means the engine's embedded preset. */
  balanceToml: string | undefined;
  balancePreset: string | undefined;
  /** The seed the start response named. Held here rather than re-read at execution, for the same
   * reason the balance is: it is part of the configuration frozen at launch, and the response that
   * carried it has been consumed by then. */
  seed: string;
  ticksObserved: number;
  /**
   * The run's own phase, mapped to the shipped `RunStatus` vocabulary by {@link progressStatus}.
   *
   * Two vocabularies rather than one, deliberately: the reducer reads `RunStatus`, which is the
   * *server's* spelling, and inventing a `'queued'` member of it would make a local phase look like
   * a batch-service state. A local run that has been admitted but not stepped is `in_flight` as far
   * as anything downstream is concerned, which is true.
   */
  phase: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  /** Why it failed, for the status mapping — a quota loss is a trace-write failure, not a
   * simulation one, and the distinction is what a player can act on. */
  failure: PracticeRunFailure | null;
  /** Memoized so two `awaitCompletion` calls for one run drive one season, not two. */
  execution: Promise<RunOutcome> | null;
}

/** The local phase in the shipped `RunStatus` spelling. */
function progressStatus(run: AdmittedRun): RunStatus {
  switch (run.phase) {
    case 'queued':
    case 'running':
      return 'in_flight';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'failed':
      return run.failure === 'quota_exhausted' ? 'trace_io_failed' : 'simulation_failed';
  }
}

/** The failure detail a surface shows. Typed reasons in, one sentence out — the caller never
 * interprets a machine word. */
function failureDetail(reason: PracticeRunFailure): string {
  switch (reason) {
    case 'quota_exhausted':
      return 'This device ran out of room for the season, even after clearing the oldest run.';
    case 'worker_crashed':
      return 'The simulation stopped unexpectedly on this device.';
    case 'invalid_configuration':
      return 'That run configuration was refused before it started.';
    case 'unavailable':
      return 'The run could not be completed on this device.';
  }
}

export function createPracticeRunController(
  deps: PracticeRunControllerDeps,
): PracticeRunController {
  const runs = new Map<number, AdmittedRun>();
  let inFlight: { runId: number; cancel: () => void } | null = null;

  function progressOf(run: AdmittedRun): RunProgressDto {
    return {
      runId: run.runId,
      status: progressStatus(run),
      ticksObserved: run.ticksObserved,
      terminalTick: run.terminalTick,
    };
  }

  /** Steps the season and, on a terminal completion only, posts the one summary. */
  async function execute(run: AdmittedRun): Promise<RunOutcome> {
    const worker = deps.createWorker();
    inFlight = { runId: run.runId, cancel: () => worker.cancel() };
    run.phase = 'running';
    let result: PracticeRunResult;
    try {
      result = await worker.run(
        {
          runId: run.runId,
          documentVersion: run.documentVersion,
          document: run.document,
          seed: run.seed,
          terminalTick: run.terminalTick,
          balanceToml: run.balanceToml,
          balancePreset: run.balancePreset,
        },
        (progress) => {
          run.ticksObserved = progress.ticksObserved;
          // The horizon can only be *narrowed* by the device, never invented: a worker that reports
          // a different terminal tick is reporting the run it actually configured.
          run.terminalTick = progress.terminalTick;
          deps.onProgress?.(run.runId);
        },
        // Relayed with the run's own id, so a view can never be attributed to a run it did not come
        // from — the same guard `progressFor(runId)` gives the count.
        (view) => deps.onWatch?.(run.runId, view),
      );
    } catch (error) {
      result = { kind: 'failed', reason: 'worker_crashed' };
      void error;
    } finally {
      worker.terminate();
      if (inFlight?.runId === run.runId) {
        inFlight = null;
      }
    }

    if (result.kind !== 'completed') {
      run.phase = result.kind === 'cancelled' ? 'cancelled' : 'failed';
      run.failure = result.kind === 'failed' ? result.reason : null;
      deps.onProgress?.(run.runId);
      // **No summary for a run that never reached a terminal state.** A cancelled run's token
      // accepts nothing (`rounds-run-summary`), so posting one would be a request built to be
      // refused — and would put a rejection in front of a player who cancelled deliberately.
      return result.kind === 'cancelled'
        ? { kind: 'failed', runId: run.runId, status: 'cancelled' }
        : {
            kind: 'failed',
            runId: run.runId,
            status: result.reason,
            detail: failureDetail(result.reason),
          };
    }

    run.phase = 'completed';
    run.ticksObserved = result.terminalTick;
    run.terminalTick = result.terminalTick;
    deps.onProgress?.(run.runId);

    // The second and last server call. Its outcome does not change the run's own outcome: the
    // season happened and is watchable on this device either way, and a refused summary costs the
    // Beekeeper its context, not the player their run.
    await deps.transport.submitRunSummary(run.runId, run.token, result.summary);

    return {
      kind: 'completed',
      runId: run.runId,
      score: result.score,
      terminalTick: result.terminalTick,
    };
  }

  return {
    async submit(candidate: RunCandidate): Promise<RunSubmission> {
      // **The compile gate, before the request.** A draft that does not compile is refused here,
      // with the compiler's own diagnostics, having sent nothing: no request, no trace, no run. The
      // server never sees a submission it would only reject, and a device with no network at all
      // still gets its diagnostics.
      //
      // This is *not* a relaxation of the run-start request below. Compiling locally buys latency
      // and honest diagnostics; the token still comes from the server, and without it no run starts
      // (`rounds-local-run`).
      const compiled = await deps.compile(candidate.document);
      if (compiled.kind === 'failed') {
        // The shipped `compileFailed` refusal, so the sandbox screen's existing blocked-Run popup
        // renders on-device diagnostics with no second vocabulary and no second code path.
        return { kind: 'refused', refusal: { kind: 'compileFailed', diagnostics: compiled.diagnostics } };
      }

      // Call one of two. It commits the document and returns the token bound to the version it
      // committed — so nothing below can attribute this run to a version it did not simulate.
      // **Neither a rival count nor a seed is sent** (`home-and-single-player`): rivals are fixed at
      // three and the seed is server configuration, which is what makes every score on the ladder
      // comparable. The seed comes back in the response below and is frozen into the run from there.
      const start = await deps.transport.startLocalRun({
        document: candidate.document,
        baseOrdinal: candidate.baseOrdinal,
      });
      if (start.kind === 'refused') {
        // No run starts, and in particular nothing has stepped: local execution begins only after
        // an accepted response.
        return start;
      }
      runs.set(start.runId, {
        runId: start.runId,
        documentVersion: start.documentVersion,
        token: start.token,
        terminalTick: start.terminalTick,
        document: candidate.document,
        // **The world, from the response, never invented here.** The device cannot resolve the
        // server's preset — the engine reaches only the one embedded in it — so an absent balance is
        // carried forward as absent and means exactly that embedded preset.
        balanceToml: start.balanceToml,
        balancePreset: start.balancePreset,
        seed: start.seed,
        ticksObserved: 0,
        phase: 'queued',
        failure: null,
        execution: null,
      });
      return { kind: 'accepted', runId: start.runId, documentVersion: start.documentVersion };
    },

    awaitCompletion(runId: number): Promise<RunOutcome> {
      const run = runs.get(runId);
      if (run === undefined) {
        // Never fabricate a completion for a run this controller never admitted.
        return Promise.resolve({ kind: 'failed', runId, status: 'unknown_run' });
      }
      run.execution ??= execute(run);
      return run.execution;
    },

    progressFor(runId: number): RunProgressDto | null {
      const run = runs.get(runId);
      return run === undefined ? null : progressOf(run);
    },

    cancel(): void {
      inFlight?.cancel();
    },
  };
}
